import { observer } from 'mobx-react-lite';

import { Info } from 'components/base/Info';
import { DetailCard } from 'components/details/DetailCard';
import { DetailList } from 'components/details/DetailList';

import type { EditableNodeDefinition } from './EditableNodeDefinition';
import type { EditableOutputMatcher } from './EditableOutputMatcher';
import { OutputMatcherFields } from './OutputMatcherFields';

interface Props {
  readonly entity: EditableNodeDefinition;
}

// The card subtitle reads matcher.summary, which changes as fields are edited. It must be its own observer: renderItem below
// runs inside DetailList's drag render-prop (outside DetailList's reaction), so a plain read there wouldn't be tracked.
const MatcherSummary = observer<{ matcher: EditableOutputMatcher }>(({ matcher }) => <>{matcher.summary}</>);

/**
 * The Output Matcher tab: an intro callout over an ordered, drag-reorderable list of matcher cards. Native MobX view over the
 * definition's `outputMatchers` Aggregate (add / remove / reorder / edit); shown only for output-producing node types.
 */
export const OutputMatcherTab = observer<Props>(({ entity }) => {
  const matchers = entity.outputMatchers;
  return (
    <div className="d-flex flex-column gap-3">
      <Info heading="Matchers are executed in order.">
        Each matcher tests either a search string against an output file or the node instance&apos;s return code; the first match acts on the node instance,
        then continues to or stops the remaining matchers as configured.
      </Info>
      <DetailList
        items={matchers}
        getKey={(matcher) => matcher.key}
        onReorder={entity.reorderOutputMatchers}
        onAdd={entity.createOutputMatcher}
        addLabel="Add matcher"
        renderItem={(matcher, index, dragHandle) => (
          <DetailCard
            index={index + 1}
            title="Matcher"
            subtitle={<MatcherSummary matcher={matcher} />}
            collapsible
            dragHandle={dragHandle}
            onMoveUp={index > 0 ? () => entity.reorderOutputMatchers(index, index - 1) : undefined}
            onMoveDown={index < matchers.length - 1 ? () => entity.reorderOutputMatchers(index, index + 1) : undefined}
            onRemove={matcher.delete}
          >
            <OutputMatcherFields matcher={matcher} />
          </DetailCard>
        )}
      />
    </div>
  );
});
